import { ChangeDetectorRef, Directive, inject, Injector } from '@angular/core';
import { ScrollSectionItem } from '@sama/ui-library';
import { Modifier } from '../../../../../../../cross-team-shared/misc-services/events/event-helper';
import { KeyboardShortcutName } from '../../../../../../../cross-team-shared/misc-services/keyboard-shortcut.service';
import { RootKeyboardShortcutService } from '../../../../../../shared/keyboard-shortcuts/root-keyboard-shortcut.service';
import { FeatureFlagService } from '../../../../../../shared/misc-services/feature-flag.service';

type Shortcut = { name: string; key: string };
type Section = { name: string; shortcuts: Shortcut[] };
type Tab = { name: string; sections: Section[] };

export interface KeyboardPanelConfig {
  tabs: Tab[];
}

@Directive()
export abstract class HelpModalComponent {
  config!: KeyboardPanelConfig;
  sections!: Array<Array<{ name: string; key?: string }>>;
  panShortcut = 'RIGHT CLICK+DRAG, W, A, S, D';

  protected isViewSettingsShortcutsOn: boolean;

  protected keyboardShortcutService: RootKeyboardShortcutService;
  private featureFlagService: FeatureFlagService;
  private changeDetectorRef = inject(ChangeDetectorRef);
  private formattedKeysMap = new Map<string, string[][]>();

  constructor(injector: Injector) {
    this.keyboardShortcutService = injector.get(RootKeyboardShortcutService);
    this.featureFlagService = injector.get(FeatureFlagService);
    this.isViewSettingsShortcutsOn = this.featureFlagService.hasFlag(
      'view_settings_shortcuts',
    );
  }

  protected shortcut(name: KeyboardShortcutName) {
    return this.keyboardShortcutService.humanReadableShortcut(name);
  }

  protected modifier(name: Modifier) {
    return this.keyboardShortcutService.humanReadableModifier(name);
  }

  protected getViewSettingsShortcuts() {
    return [
      { name: 'Decrease opacity', key: this.shortcut('decreaseOpacity') },
      { name: 'Increase opacity', key: this.shortcut('increaseOpacity') },
    ];
  }

  protected getBrightnessAndContrastShortcuts() {
    return [
      {
        name: 'Decrease brightness',
        key: this.shortcut('decreaseBrightness'),
      },
      {
        name: 'Increase brightness',
        key: this.shortcut('increaseBrightness'),
      },
      {
        name: 'Decrease contrast',
        key: 'SHIFT + -',
      },
      {
        name: 'Increase contrast',
        key: 'SHIFT + =',
      },
      {
        name: 'Reset brightness and contrast',
        key: '- + =',
      },
    ];
  }

  protected getLayerViewSettingsShortcuts() {
    return [
      {
        name: 'View all/selected/none',
        key: this.shortcut('cycleViewMode'),
      },
    ];
  }

  protected getInspectModeShortcut() {
    return [{ name: 'Inspect mode', key: this.shortcut('toggleInspectMode') }];
  }

  protected getVisibilityShortcut() {
    return [
      {
        name: 'Change shape visibility',
        key: this.shortcut('changeShapeVisibility'),
      },
    ];
  }

  protected getUndoRedoShortcuts() {
    return [
      { name: 'Undo', key: this.shortcut('undo') },
      { name: 'Redo', key: this.shortcut('redo') },
    ];
  }

  protected getScrubberShortcuts() {
    return [
      { name: 'Forward one frame', key: this.shortcut('scrubForward') },
      { name: 'Forward five frames', key: this.shortcut('skipScrubForward') },
      {
        name: 'Go to next item',
        key: this.shortcut('smartSkipForward'),
      },
      { name: 'Back one frame', key: this.shortcut('scrubBackward') },
      { name: 'Back five frames', key: this.shortcut('skipScrubBackward') },
      {
        name: 'Go to previous item',
        key: this.shortcut('smartSkipBackward'),
      },
      { name: 'Copy last keyframe', key: this.shortcut('copyLastKeyframe') },
      { name: 'Insert keyframe', key: this.shortcut('toggleKeyframe') },
      { name: 'Play/Pause', key: this.shortcut('playPause') },
    ];
  }

  protected getEditShortcuts() {
    return [
      { name: 'Scope object', key: this.shortcut('scopeObject') },
      {
        name: 'Select next object',
        key: this.shortcut('selectNextAnnotation'),
      },
      {
        name: 'Select previous object',
        key: this.shortcut('selectPreviousAnnotation'),
      },
      { name: 'Paste shapes', key: this.shortcut('pasteShapes') },
      { name: 'Select all', key: this.shortcut('selectAllShapes') },
      {
        name: 'Multi-select',
        key: `${this.modifier('platform')}+LEFT CLICK`,
      },
      { name: 'Clear selection', key: this.shortcut('clearSelection') },
      { name: 'Cancel shape', key: this.shortcut('cancelTool') },
      { name: 'Delete shapes', key: this.shortcut('deleteShapes') },
    ];
  }

  protected getMovementShortcuts() {
    return [
      {
        name: 'Nudge by 5px',
        key: 'Select Shape + W, A, S, D',
      },
      {
        name: 'Nudge by 1px',
        key: `Select Shape + ${this.modifier('shiftKey')} + W, A, S, D`,
      },
      {
        name: 'Expand by 1px',
        key: `Select Shape + ${this.modifier('altKey')} + W, D`,
      },
      {
        name: 'Contract by 1px',
        key: `Select Shape + ${this.modifier('altKey')} + A, S`,
      },
      {
        name: 'Move slider point by 5px',
        key: 'Select Shape + Q, E',
      },
      {
        name: 'Move slider point by 1px',
        key: `Select Shape + ${this.modifier('shiftKey')} + Q, E`,
      },
    ];
  }

  protected getDeleteSubpointsShortcut() {
    return [
      {
        name: 'Delete subpoints',
        key: `${this.modifier('altKey')}+CLICK+DRAG with edit tool`,
      },
    ];
  }

  protected getSectionId(tabName: string, sectionName: string) {
    return `section_${tabName}_${sectionName}`;
  }

  protected formatKey(tabName: string, shortcutName: string, key: string) {
    const id = `${tabName}_${shortcutName}`;
    if (this.formattedKeysMap.has(id)) {
      return this.formattedKeysMap.get(id);
    }

    let formattedKey: string[][];
    if (key === ',') {
      formattedKey = [[',']];
    } else {
      const shortcuts = key.split(',');
      formattedKey = shortcuts.map((shortcut) =>
        shortcut.split('+').map((k) => k.trim()),
      );
    }
    this.formattedKeysMap.set(id, formattedKey);
    return formattedKey;
  }

  protected getScrollingItems(tab: Tab): ScrollSectionItem[] {
    return tab.sections.map((section) => ({
      id: this.getSectionId(tab.name, section.name),
      label: section.name,
      type: 'item',
    }));
  }

  protected onScroll() {
    // This is required to update the sidebar items highlight when scrolling
    // because the onpush change detection strategy doesn't detect the
    // scrolling changes
    setTimeout(() => this.changeDetectorRef.detectChanges());
  }
}
